import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import SubUserRoute from "./components/SubUserRoute";
import WalletConnectModal from "./components/WalletConnectModal";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import VolumeBot from "./pages/VolumeBot";
import LureBot from "./pages/LureBot";
import TokenSafety from "./pages/TokenSafety";
import BotEarnings from "./pages/BotEarnings";
import Analytics from "./pages/Analytics";
import ZKVault from "./pages/ZKVault";
import History from "./pages/History";
import DefiExchanges from "./pages/DefiExchanges";
import AdminDashboard from "./pages/AdminDashboard";
import AdminConfig from "./pages/AdminConfig";
import AdminGlabs from "./pages/AdminGlabs";
import ExchangeLogin from "./pages/ExchangeLogin";
import Portfolio from "./pages/Portfolio";
import Staking from "./pages/Staking";
import CommanderLayout from "./pages/CommanderLayout";
import AffiliatePanel from "./pages/AffiliatePanel";
import AdminGodMode from "./pages/AdminGodMode";
import TradingPortal from "./pages/TradingPortal";
import BuyCredits from "./pages/BuyCredits";

// ── WalletGate — blocks dashboard access until Phantom is connected ──────────
// Uses sessionStorage: cleared automatically when browser tab closes.
// On every new session (tab open), user must re-connect Phantom.
function WalletGate({ children }) {
  const { user, loading } = useAuth();
  const [connected, setConnected] = useState(
    !!sessionStorage.getItem('phantom_connected')
  );

  // Still loading auth — show spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,8%)]">
        <div className="w-8 h-8 border-2 border-[#E8520F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in — let downstream route handle redirect
  if (!user) return children;

  // Logged in but Phantom not connected this session — show mandatory modal
  if (!connected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,8%)]"
           style={{ background: 'radial-gradient(ellipse at 50% 0%, #E8520F0A 0%, transparent 60%), hsl(220,20%,8%)' }}>
        <div className="text-center mb-8 absolute top-8 left-1/2 -translate-x-1/2">
          <p className="text-[hsl(215,16%,56%)] font-mono text-sm">
            GauriLabs Commander · Wallet verification required
          </p>
        </div>
        <WalletConnectModal
          open={true}
          required
          onClose={() => {}} // Cannot dismiss — wallet is mandatory
          onConnected={(address) => {
            sessionStorage.setItem('phantom_connected', address);
            setConnected(true);
          }}
        />
      </div>
    );
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* SSO entry point */}
            <Route path="/sso" element={<SSOHandler />} />

            {/* ── SUB-USER TRADING PORTAL — wallet required ── */}
            <Route
              path="/trade"
              element={
                <SubUserRoute>
                  <WalletGate>
                    <TradingPortal />
                  </WalletGate>
                </SubUserRoute>
              }
            >
              <Route index element={<Navigate to="/trade/volume-bot" replace />} />
              <Route path="volume-bot" element={<VolumeBot />} />
              <Route path="lure-bot" element={<LureBot />} />
              <Route path="token-safety" element={<TokenSafety />} />
              <Route path="earnings" element={<BotEarnings />} />
              <Route path="buy-credits" element={<BuyCredits />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="portfolio" element={<Portfolio />} />
              <Route path="exchanges" element={<DefiExchanges />} />
              <Route path="exchange-login" element={<ExchangeLogin />} />
              <Route path="history" element={<History />} />
            </Route>

            {/* ── ADMIN DASHBOARD — wallet required ── */}
            <Route
              path="/dashboard"
              element={
                <AdminRoute>
                  <WalletGate>
                    <Dashboard />
                  </WalletGate>
                </AdminRoute>
              }
            >
              <Route path="admin" element={<AdminDashboard />} />
              <Route path="volume-bot" element={<VolumeBot />} />
              <Route path="lure-bot" element={<LureBot />} />
              <Route path="token-safety" element={<TokenSafety />} />
              <Route path="bot-earnings" element={<BotEarnings />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="zk-vault" element={<ZKVault />} />
              <Route path="history" element={<History />} />
              <Route path="defi-exchanges" element={<DefiExchanges />} />
              <Route path="exchange-login" element={<ExchangeLogin />} />
              <Route path="portfolio" element={<Portfolio />} />
              <Route path="staking" element={<Staking />} />
              <Route path="admin-config" element={<AdminConfig />} />
              <Route path="god-mode" element={<AdminGodMode />} />
              <Route path="affiliate-sla" element={<AffiliatePanel />} />
              <Route path="glabs" element={<AdminGlabs />} />
            </Route>

            {/* Commander role-based shell */}
            <Route path="/commander" element={<CommanderLayout />}>
              <Route path="admin" element={<AdminGodMode />} />
              <Route path="admin/*" element={<AdminGodMode />} />
              <Route path="affiliate" element={<AffiliatePanel />} />
              <Route path="affiliate/*" element={<AffiliatePanel />} />
            </Route>

            <Route path="/" element={<RoleRedirect />} />
            <Route path="*" element={<RoleRedirect />} />
          </Routes>
        </BrowserRouter>

        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            style: {
              background: 'hsl(220, 18%, 11%)',
              border: '1px solid hsl(220, 16%, 18%)',
              color: 'hsl(210, 20%, 92%)',
            }
          }}
        />
      </div>
    </AuthProvider>
  );
}

function SSOHandler() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,8%)]">
      <div className="text-center space-y-4">
        <div className="w-10 h-10 border-2 border-[#E8520F] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-[hsl(215,16%,56%)] font-mono text-sm">Authenticating via GauriLabs...</p>
      </div>
    </div>
  );
}

function RoleRedirect() {
  return <Navigate to="/dashboard" replace />;
}

export default App;
